/* eslint-disable react/no-unescaped-entities */
import PillarPage from "./PillarPage";

const HERO = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600";

const TOC = [
  { id: "eligibility", label: "Can an expat get a Dubai mortgage?" },
  { id: "ltv", label: "Loan-to-value & down payment" },
  { id: "rates", label: "Interest rates in 2026" },
  { id: "documents", label: "Documents required" },
  { id: "banks", label: "Best UAE mortgage banks" },
  { id: "process", label: "Step-by-step process" },
  { id: "hidden-costs", label: "Hidden costs to budget for" },
  { id: "non-resident", label: "Buying from abroad (non-resident)" },
];

const SECTIONS = [
  {
    id: "eligibility",
    heading: "Yes — expats can get a mortgage in Dubai. Here's who qualifies.",
    body: (
      <>
        <p>
          UAE banks lend to expat residents and non-residents alike. Standard eligibility in 2026:
        </p>
        <ul>
          <li><b>Minimum age:</b> 21. Maximum age at loan maturity: 65 (salaried), 70 (self-employed).</li>
          <li><b>Minimum monthly income:</b> AED 15,000 (salaried) or AED 25,000 (self-employed) — most Tier-1 banks.</li>
          <li><b>Employment:</b> 6+ months with current employer OR 2 years running your own business.</li>
          <li><b>Credit history:</b> Clean AECB report (Al Etihad Credit Bureau). Any credit-card default in the past 24 months is a hard no.</li>
          <li><b>Debt Burden Ratio (DBR):</b> Total monthly obligations (including new mortgage EMI) must stay under 50% of monthly income — a UAE Central Bank rule.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ltv",
    heading: "Down payment & loan-to-value (LTV) rules — UAE Central Bank",
    body: (
      <table>
        <thead>
          <tr><th>Buyer type</th><th>Property value</th><th>Max LTV</th><th>Min down payment</th></tr>
        </thead>
        <tbody>
          <tr><td>UAE resident, 1st property</td><td>Up to AED 5M</td><td>80%</td><td>20%</td></tr>
          <tr><td>UAE resident, 1st property</td><td>Above AED 5M</td><td>70%</td><td>30%</td></tr>
          <tr><td>UAE resident, 2nd property onwards</td><td>Any</td><td>65%</td><td>35%</td></tr>
          <tr><td>Non-resident buyer</td><td>Any</td><td>50–60%</td><td>40–50%</td></tr>
          <tr><td>Off-plan purchase</td><td>Any</td><td>50%</td><td>50%</td></tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "rates",
    heading: "Interest rates in 2026 — variable vs fixed",
    body: (
      <>
        <p>Point Penta's mortgage desk currently quotes the following ranges from Tier-1 lenders:</p>
        <ul>
          <li><b>Variable rate (EIBOR + margin):</b> 4.5% – 6.5% per annum. Tracks 3-month EIBOR (currently ~4.1%) + a 0.5–2.5% bank margin.</li>
          <li><b>Fixed rate (1–5 years):</b> 5.0% – 6.5% per annum. Locks your EMI for the fixed period, then reverts to variable.</li>
          <li><b>Hybrid (fixed then variable):</b> Best of both — 3-yr fixed at 4.99%, then EIBOR + 1.5%.</li>
        </ul>
        <p>
          Most Point Penta clients choose the <b>3-year fixed then variable</b> structure — it caps risk during the settling-in period, then lets you refinance if rates drop.
        </p>
      </>
    ),
  },
  {
    id: "documents",
    heading: "Documents you'll need",
    body: (
      <ul>
        <li>Passport + Emirates ID (residents) OR passport + entry stamp (non-residents)</li>
        <li>Salary certificate — issued within the last 30 days</li>
        <li>Last 6 months of bank statements</li>
        <li>Last 6 months of salary transfer proof</li>
        <li>Recent utility bill (address verification)</li>
        <li>Existing credit facility statements (credit cards, personal loans)</li>
        <li>Property details (MoU / Form F / SPA) once identified</li>
        <li>For self-employed: 2 years of company financials + trade licence</li>
      </ul>
    ),
  },
  {
    id: "banks",
    heading: "Best UAE mortgage banks in 2026",
    body: (
      <table>
        <thead>
          <tr><th>Bank</th><th>Best for</th><th>Standout feature</th></tr>
        </thead>
        <tbody>
          <tr><td>Emirates NBD</td><td>UAE resident salaried</td><td>Widest range of fixed-rate products, fast pre-approval (48hrs)</td></tr>
          <tr><td>Mashreq</td><td>Non-residents</td><td>50% LTV to non-residents, streamlined offshore process</td></tr>
          <tr><td>HSBC</td><td>International buyers</td><td>Global relationship pricing, multi-currency income accepted</td></tr>
          <tr><td>ADCB</td><td>Off-plan buyers</td><td>Bespoke off-plan products with 50% LTV, milestone-linked releases</td></tr>
          <tr><td>Standard Chartered</td><td>High-net-worth expats</td><td>Priority Private Banking rates from 4.49% fixed</td></tr>
          <tr><td>ADIB / Dubai Islamic Bank</td><td>Sharia-compliant financing</td><td>Ijara / Murabaha structures — no conventional interest</td></tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "process",
    heading: "Step-by-step process — from enquiry to keys",
    body: (
      <ol>
        <li><b>Pre-qualification (Day 1–3):</b> Submit documents to 2–3 banks in parallel. Receive a "mortgage-in-principle" letter valid 60–90 days.</li>
        <li><b>Property search (Day 4–14):</b> With MIP in hand, shortlist and make offers. Point Penta pre-vets every mandate for mortgage eligibility.</li>
        <li><b>Property valuation (Day 15–21):</b> Bank commissions a licensed valuer. Fee: AED 2,500–3,500. Valuation must match sale price (or you top up the gap).</li>
        <li><b>Final offer letter (Day 22–25):</b> Bank issues formal offer. You sign and pay processing fee.</li>
        <li><b>Property transfer at DLD (Day 26–35):</b> Bank issues manager cheque. You + seller + bank rep sign. Title deed transfers same day. Keys in hand.</li>
      </ol>
    ),
  },
  {
    id: "hidden-costs",
    heading: "Hidden costs beyond the down payment",
    body: (
      <table>
        <thead><tr><th>Item</th><th>Cost</th></tr></thead>
        <tbody>
          <tr><td>Bank processing fee</td><td>0.5% – 1% of loan amount</td></tr>
          <tr><td>Property valuation (bank-appointed)</td><td>AED 2,500 – 3,500</td></tr>
          <tr><td>Mortgage registration (DLD)</td><td>0.25% of loan + AED 290</td></tr>
          <tr><td>Life insurance (mandatory)</td><td>0.3% – 0.7% of loan p.a.</td></tr>
          <tr><td>Property insurance</td><td>AED 500 – 1,500 p.a.</td></tr>
          <tr><td>Early settlement fee (if you refinance)</td><td>1% of outstanding OR AED 10,000 cap (whichever lower)</td></tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "non-resident",
    heading: "Buying from abroad — non-resident mortgage playbook",
    body: (
      <ol>
        <li><b>Choose a bank that lends to non-residents:</b> Mashreq, HSBC, Standard Chartered, ADCB.</li>
        <li><b>Open a UAE bank account remotely</b> — required for EMI direct debit. Point Penta can facilitate via our banking partners without you flying in.</li>
        <li><b>Get pre-approved before you visit Dubai</b> — turns your viewing trip into an offer-ready trip.</li>
        <li><b>Sign the SPA in your home country</b> via Power of Attorney if you can't fly. UAE embassy will attest the POA.</li>
        <li><b>Complete the DLD transfer via representative</b> — one Point Penta advisor holds POA on your behalf, closes in your absence.</li>
      </ol>
    ),
  },
];

const FAQS = [
  { q: "Can an expat get a mortgage in Dubai in 2026?", a: "Yes. UAE banks lend to expat residents at up to 80% LTV (for a first property below AED 5M) with a 20% down payment. Non-residents get up to 50–60% LTV. Minimum salary is typically AED 15,000/month, and total debt (including the new mortgage) must stay below 50% of income per UAE Central Bank rules." },
  { q: "How much down payment do I need for a Dubai property?", a: "20% for UAE residents buying a first property under AED 5M. 30% if the property is over AED 5M. 35% for a second property. 40–50% if you're a non-resident buyer. 50% for off-plan. These are UAE Central Bank minimums — you can always put more down." },
  { q: "What is the current mortgage interest rate in Dubai?", a: "Point Penta's mortgage desk quotes 4.5–6.5% variable (EIBOR + 0.5–2.5% margin) and 5.0–6.5% fixed for 1–5 years in 2026. Best rates go to salaried UAE residents with clean AECB credit reports; non-residents pay a 50–100 basis-point premium." },
  { q: "Which bank offers the best mortgage in Dubai?", a: "For salaried UAE residents: Emirates NBD (fastest approval + widest fixed-rate range). For non-residents: Mashreq or HSBC. For high-net-worth expats: Standard Chartered Priority Private. For Sharia-compliant financing: ADIB or Dubai Islamic Bank. Point Penta arranges pre-approval with 2–3 banks in parallel." },
  { q: "How long does it take to get a Dubai mortgage?", a: "Pre-qualification: 48–72 hours. Full approval after property identification: 2–3 weeks. Property transfer at DLD: 30–45 days total from initial application to keys in hand. Non-resident applications add roughly 1 week for KYC and offshore documentation." },
  { q: "Can I get a mortgage as a non-resident?", a: "Yes — Mashreq, HSBC, ADCB, and Standard Chartered actively lend to non-residents at 50–60% LTV. You'll need to open a UAE bank account (can be done remotely), provide income proof from your home country, and typically pay a slight premium on rates versus resident borrowers." },
  { q: "What's the total cost of a Dubai mortgage beyond the interest?", a: "Bank processing fee: 0.5–1% of loan. Valuation: AED 2,500–3,500. DLD mortgage registration: 0.25% of loan + AED 290. Mandatory life insurance: 0.3–0.7% p.a. Property insurance: AED 500–1,500 p.a. Budget roughly 2% of the loan amount in one-off setup costs." },
  { q: "Can I refinance my Dubai mortgage later?", a: "Yes. Early settlement fees are capped by UAE Central Bank at 1% of the outstanding balance or AED 10,000 (whichever is lower). If rates drop 100+ basis points, refinancing typically pays back within 12–18 months. Point Penta's mortgage desk reviews client mortgages annually." },
];

export default function DubaiMortgagePillar() {
  return (
    <PillarPage
      slug="dubai-mortgage-for-expats-2026"
      category="Financing"
      title="Dubai Mortgage for Expats — 2026 Complete Guide"
      hero={HERO}
      updated="Feb 2026"
      intro="Expats and non-residents can absolutely get a mortgage in Dubai in 2026 — at up to 80% LTV for residents and 50–60% LTV for non-residents. This guide walks through the UAE Central Bank rules, current 2026 rates, the six banks worth talking to, and the exact 30–45 day process from pre-qualification to keys in hand."
      toc={TOC}
      sections={SECTIONS}
      faqs={FAQS}
    />
  );
}
